/* eslint-disable */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Skeleton, CircularProgress,
} from '@mui/material';
// Document modals hidden — functionality not yet developed
// import PieceReglementModal from './PieceReglementModal';
// import AttestationImpayeModal from './AttestationImpayeModal';
import { useParams, useHistory } from 'react-router-dom';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import iconEye from '../../Images/Icon_eye.svg';
// import useNetworkLoader from 'hooks/useNetworkLoader';
import { formatAmount } from 'Utils/ulilities';
import { getReglementById } from './ReglementServices';
import { downloadFacturePdf } from '../Factures/FacturesServices';
import toast from 'Utils/toast';

/* ── Status config ───────────────────────────────────────────────── */
const STATUS_CONFIG = {
  PAYE:   { label: 'Payée',   color: '#12B981', bg: '#ECFEF6',    border: '#12B981' },
  IMPAYE: { label: 'Impayée', color: '#EF456E', bg: '#EF456E0D',  border: '#EF456E' },
};

const resolveStatutKey = (statut) => {
  if (!statut) return null;
  const s = statut.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  if (s.includes('impaye')) return 'IMPAYE';
  if (s.includes('paye')) return 'PAYE';
  return statut.toUpperCase();
};

const StatusBadge = ({ statut }) => {
  const key = resolveStatutKey(statut);
  const cfg = STATUS_CONFIG[key] || { label: statut || '—', color: '#666', bg: '#F5F5F5', border: '#CCC' };
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', px: '10px', py: '4px', borderRadius: '20px', border: `1px solid ${cfg.border}`, backgroundColor: cfg.bg, whiteSpace: 'nowrap' }}>
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: cfg.color }} />
      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: cfg.color }}>{cfg.label}</Typography>
    </Box>
  );
};

/* ── Date formatter ─────────────────────────────────────────────── */
const formatDetailDate = (date) => {
  if (!date) return '—';
  const d = new Date(date);
  const parts = d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  return parts.charAt(0).toUpperCase() + parts.slice(1);
};

/* ── Table header cell ───────────────────────────────────────────── */
const TH = ({ children, width, sortKey, onSort, activeSortKey, sortDir, showChevron }) => {
  const isActive = sortKey && activeSortKey === sortKey;
  return (
    <TableCell
      sx={{ fontWeight: 600, fontSize: '14px', color: '#385576', letterSpacing: '0.5px', textTransform: 'uppercase', borderBottom: '1px solid #D1D5DB', py: '8px', px: '12px', width, whiteSpace: 'nowrap', cursor: sortKey ? 'pointer' : 'default', userSelect: 'none', backgroundColor: '#FFFFFF', '&:first-of-type': { pl: '24px' } }}
      onClick={sortKey && onSort ? () => onSort(sortKey) : undefined}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '1px' }}>
        {children}
        {showChevron && sortKey && (
          isActive && sortDir === 'asc'
            ? <KeyboardArrowUpIcon sx={{ fontSize: 16, color: '#385576' }} />
            : <KeyboardArrowDownIcon sx={{ fontSize: 16, color: '#385576' }} />
        )}
      </Box>
    </TableCell>
  );
};

const TD = ({ children }) => (
  <TableCell sx={{ fontSize: '16px', color: '#000000', fontWeight: 600, borderBottom: '1px solid #D1D5DB', py: '12px', px: '12px', whiteSpace: 'nowrap', '&:first-of-type': { pl: '24px' } }}>
    {children}
  </TableCell>
);

/* ── Info card ───────────────────────────────────────────────────── */
const InfoCard = ({ label, value, valueColor, isAlert }) => (
  <Box
    sx={{ backgroundColor: '#FFFFFF', border: `1.5px solid ${isAlert ? '#F26A8B' : '#D1D5DC'}`, borderRadius: '10px', px: '16px', py: '12px', boxShadow: '0px 2px 8px rgba(0,0,0,0.07)' }}
  >
    <Typography sx={{ fontSize: '14px', color: '#8398AE', fontWeight: 600, mb: '4px' }}>{label}</Typography>
    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: valueColor || '#385576' }}>{value}</Typography>
  </Box>
);

/* ── Main component ──────────────────────────────────────────────── */
const ReglementDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  // const { callApi } = useNetworkLoader();

  const [reglement, setReglement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  // Document modal state hidden — functionality not yet developed
  // const [openPiece, setOpenPiece] = useState(false);
  // const [openAttestation, setOpenAttestation] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    console.log('[ReglementDetail] fetching id:', id);
    getReglementById(id)
      .then((data) => { console.log('[ReglementDetail] data:', data); setReglement(data); })
      .catch((err) => { console.error('[ReglementDetail] error:', err?.response?.status, err?.response?.data, err); toast.error('Règlement introuvable'); history.push('/reglements'); })
      .finally(() => setLoading(false));
  }, [id]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  // Document viewing handlers hidden — functionality not yet developed
  // const handleViewDocument = () => { ... };
  // const handleViewCertificate = () => { ... };

  const handleFacturePdf = (reference, mode) => {
    setLoadingPdf(`${reference}-${mode}`);
    downloadFacturePdf(reference)
      .then((blob) => {
        const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        if (mode === 'view') {
          window.open(url, '_blank');
        } else {
          const a = document.createElement('a');
          a.href = url;
          a.download = `${reference}.pdf`;
          a.click();
          window.URL.revokeObjectURL(url);
        }
      })
      .catch(() => toast.error('Impossible de charger le document'))
      .finally(() => setLoadingPdf(null));
  };

  const invoices = reglement?.invoices || [];

  const sortedDocs = useMemo(() => {
    return [...invoices].sort((a, b) => {
      let av, bv;
      if (sortKey === 'date') { av = new Date(a.date || 0); bv = new Date(b.date || 0); }
      else if (['montantTtc', 'montantAttache'].includes(sortKey)) { av = Number(a[sortKey] || 0); bv = Number(b[sortKey] || 0); }
      else { av = (a[sortKey] || '').toString().toLowerCase(); bv = (b[sortKey] || '').toString().toLowerCase(); }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [invoices, sortKey, sortDir]);

  if (loading) return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F8FAFC', pt: '90px', px: { xs: '16px', md: '40px' }, pb: '40px', mt: '-80px' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: '24px' }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '4px' }}>
            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#385576' }}>
              Détails du règlement : <Skeleton component="span" variant="text" width={100} sx={{ display: 'inline-block', verticalAlign: 'middle' }} />
            </Typography>
            <Skeleton variant="rounded" width={70} height={26} sx={{ borderRadius: '20px' }} />
          </Box>
          <Typography sx={{ fontSize: '16px', color: '#385576', fontWeight: 600 }}>
            Montant total net TTC : <Skeleton component="span" variant="text" width={120} sx={{ display: 'inline-block', verticalAlign: 'middle' }} />
          </Typography>
        </Box>
        {/* Skeleton for document buttons — hidden until functionality is developed */}
      </Box>

      {/* Body */}
      <Box sx={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
        {/* Left — table */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#000000', mb: '12px' }}>
            Documents liés (<Skeleton component="span" variant="text" width={24} sx={{ display: 'inline-block', verticalAlign: 'middle' }} />)
          </Typography>
          <Box sx={{ backgroundColor: '#fff', border: '1px solid #D1D5DB', borderRadius: '10px', overflow: 'hidden' }}>
            <Table>
              <TableHead>
                <TableRow>
                  {['N° Facture', 'Date', 'Montant TTC', 'Montant Attaché', 'Actions'].map((label) => (
                    <TableCell key={label} sx={{ fontWeight: 600, fontSize: '14px', color: '#385576', letterSpacing: '0.5px', textTransform: 'uppercase', borderBottom: '1px solid #D1D5DB', py: '8px', px: '12px', backgroundColor: '#fff', '&:first-of-type': { pl: '24px' } }}>
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i} sx={{ backgroundColor: i % 2 === 0 ? '#fff' : '#F8FAFC' }}>
                    {[180, 90, 100, 110, 60].map((w, j) => (
                      <TableCell key={j} sx={{ borderBottom: '1px solid #D1D5DB', py: '12px', px: '12px', '&:first-of-type': { pl: '24px' } }}>
                        <Skeleton variant="text" width={w} height={18} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>

        {/* Right — info cards */}
        <Box sx={{ width: '433px', flexShrink: 0 }}>
          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#000000', mb: '12px' }}>Informations</Typography>
          {[['Type', 80], ["Date d'émission", 160], ["Date d'échéance", 160]].map(([label, w]) => (
            <Box key={label} sx={{ backgroundColor: '#fff', border: '1.5px solid #D1D5DC', borderRadius: '10px', px: '16px', py: '12px', mb: '12px' }}>
              <Typography sx={{ fontSize: '14px', color: '#8398AE', fontWeight: 600, mb: '4px' }}>{label}</Typography>
              <Skeleton variant="text" width={w} height={20} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
  if (!reglement) return null;

  const statutKey = resolveStatutKey(reglement.statut);
  const isImpaye = statutKey === 'IMPAYE';

  const typeLabel = reglement.type
    ? reglement.type.charAt(0) + reglement.type.slice(1).toLowerCase()
    : '—';

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F8FAFC', pt: '90px', px: { xs: '16px', md: '40px' }, pb: '40px', mt: '-80px' }}>

      {/* ── Page header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: '24px' }}>
        <Box>
          <Box onClick={() => history.goBack()} sx={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', mb: '8px' }}>
            <ArrowBackIosNewIcon sx={{ fontSize: 11, color: '#8397AE' }} />
            <Typography sx={{ fontSize: '14px', color: '#8397AE', fontWeight: 600 }}>Retour aux règlements</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '4px' }}>
            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#385576' }}>
              Détails du règlement :{' '}
              <Box component="span" sx={{ color: '#000000' }}>{reglement.reference}</Box>
            </Typography>
            <StatusBadge statut={reglement.statut} />
          </Box>

          <Typography sx={{ fontSize: '16px', color: '#385576', fontWeight: 600 }}>
            Montant total net TTC :{' '}
            <Box component="span" sx={{ color: '#007BC6', fontWeight: 700 }}>
              {formatAmount(reglement.amount || 0)} DH
            </Box>
            {isImpaye && (
              <>
                {' = Attaché: '}
                <Box component="span" sx={{ fontWeight: 600, color: '#385576' }}>
                  {formatAmount(reglement.montantAttache || 0)}
                </Box>
                {' + '}
                <Box component="span" sx={{ color: '#EF456E', fontWeight: 700 }}>
                  Restant: {formatAmount(reglement.montantRestant || 0)}
                </Box>
              </>
            )}
          </Typography>
        </Box>

        {/* Document viewing buttons hidden — functionality not yet developed */}
        {/* <Box sx={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
          <Button onClick={handleViewDocument} ...>Afficher la pièce du règlement</Button>
          {isImpaye && <Button onClick={handleViewCertificate} ...>Afficher l'attestation d'impayé</Button>}
        </Box> */}
      </Box>

      {/* ── Body: table + info panel ── */}
      <Box sx={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>

        {/* Left — Invoices table */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#000000', mb: '12px' }}>
            Documents liés ({invoices.length})
          </Typography>

          <Box sx={{ backgroundColor: '#fff', border: '1px solid #D1D5DB', borderRadius: '10px', overflow: 'hidden', boxShadow: '0px 2px 8px rgba(0,0,0,0.07)' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TH width={200} sortKey="reference" onSort={handleSort} activeSortKey={sortKey} sortDir={sortDir}>N° Facture</TH>
                    <TH width={200} sortKey="date" onSort={handleSort} activeSortKey={sortKey} sortDir={sortDir} showChevron>Date</TH>
                    <TH width={180} sortKey="montantTtc" onSort={handleSort} activeSortKey={sortKey} sortDir={sortDir}>Montant TTC</TH>
                    <TH width={180} sortKey="montantAttache" onSort={handleSort} activeSortKey={sortKey} sortDir={sortDir}>Montant Attaché</TH>
                    <TH width={110}>Actions</TH>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedDocs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: 'center', py: '40px', color: '#8F8F8F', fontSize: '14px' }}>
                        Aucun document lié
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedDocs.map((doc, idx) => {
                      const bgColor = idx % 2 === 0 ? '#fff' : '#F8FAFC';
                      return (
                        <TableRow key={doc.reference || idx} sx={{ backgroundColor: bgColor, '&:hover': { backgroundColor: '#EBF5FB' }, transition: 'background-color 0.15s' }}>
                          <TD>{doc.reference || '—'}</TD>
                          <TD>{doc.date ? new Date(doc.date).toLocaleDateString('fr-FR') : '—'}</TD>
                          <TD>{formatAmount(doc.montantTtc || 0)}</TD>
                          <TD>{formatAmount(doc.montantAttache || 0)}</TD>
                          <TableCell sx={{ borderBottom: '1px solid #D1D5DB', py: '12px', px: '12px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                              <Tooltip title="Voir" placement="top" arrow>
                                <Box onClick={() => !loadingPdf && handleFacturePdf(doc.reference, 'view')} sx={{ display: 'inline-flex', cursor: loadingPdf === `${doc.reference}-view` ? 'default' : 'pointer', '&:hover img': { filter: 'brightness(0) saturate(100%) invert(29%) sepia(89%) saturate(876%) hue-rotate(182deg) brightness(100%) contrast(101%)', transition: 'filter 0.15s' }, '& img': { transition: 'filter 0.15s' } }}>
                                  {loadingPdf === `${doc.reference}-view`
                                    ? <CircularProgress size={18} sx={{ color: '#007BC6' }} />
                                    : <img src={iconEye} alt="view" style={{ width: 20, height: 20, objectFit: 'contain' }} />}
                                </Box>
                              </Tooltip>
                              <Tooltip title="Télécharger" placement="top" arrow>
                                <Box onClick={() => !loadingPdf && handleFacturePdf(doc.reference, 'download')} sx={{ display: 'inline-flex', cursor: loadingPdf === `${doc.reference}-download` ? 'default' : 'pointer', color: '#8397AE', '&:hover': { color: '#007BC6' }, transition: 'color 0.15s' }}>
                                  {loadingPdf === `${doc.reference}-download`
                                    ? <CircularProgress size={18} sx={{ color: '#007BC6' }} />
                                    : <FileDownloadOutlinedIcon sx={{ fontSize: 20 }} />}
                                </Box>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>

        {/* Right — Informations panel */}
        <Box sx={{ width: '433px', flexShrink: 0, mb: '8px' }}>
          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#000000', mb: '12px' }}>
            Informations
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <InfoCard label="Type" value={typeLabel} />
            <InfoCard label="Date d'émission" value={formatDetailDate(reglement.rgDate)} />
            <InfoCard label="Date d'échéance" value={formatDetailDate(reglement.dateEcheance)} />
            {isImpaye && reglement.rgImpaye && (
              <InfoCard
                label="Date d'impayé"
                value={formatDetailDate(reglement.rgImpaye)}
                valueColor="#EF456E"
                isAlert
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Document modals hidden — functionality not yet developed */}
    </Box>
  );
};

export default ReglementDetail;
